import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AppDataGrid from "../../../common/AppDataGrid";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";
import CcpCpBreadcrumbs from "../../../Layout/inc/CcpCpBreadcrumbs";
import { collection, getDocs, writeBatch, doc } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { formatDateTime } from "../../../../utils/formatDate";

const COLLECTION_NAME = "CoursePlanningForm";
const EXCLUDED_FIELDS = ["id", "createdBy"];

// Firestore only allows 500 writes per batch — chunk deletes to stay under that.
const BATCH_CHUNK_SIZE = 400;

const formatCellValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (
      typeof value.toDate === "function" ||
      typeof value.seconds === "number"
    ) {
      return formatDateTime(value);
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const CoursePlanningFormEntries = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));

      const fetchedRows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const fieldNames = new Set();
      fetchedRows.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (!EXCLUDED_FIELDS.includes(key)) fieldNames.add(key);
        });
      });

      const generatedColumns = Array.from(fieldNames)
        .sort((a, b) => a.localeCompare(b))
        .map((field) => ({
          field,
          headerName: field,
          minWidth: 160,
          flex: 1,
          valueFormatter: (value) => formatCellValue(value),
        }));

      setColumns(generatedColumns);
      setRows(fetchedRows);
    } catch (err) {
      console.error("Error fetching CoursePlanningForm entries:", err);
      setError("Failed to load course planning form entries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleConfirmDelete = async () => {
    const idsToDelete = selectionModel;
    setDeleting(true);
    try {
      for (let i = 0; i < idsToDelete.length; i += BATCH_CHUNK_SIZE) {
        const batch = writeBatch(db);
        idsToDelete.slice(i, i + BATCH_CHUNK_SIZE).forEach((id) => {
          batch.delete(doc(db, COLLECTION_NAME, id));
        });
        await batch.commit();
      }

      setSnackbar({
        open: true,
        message: `Deleted ${idsToDelete.length} entr${
          idsToDelete.length === 1 ? "y" : "ies"
        }.`,
        severity: "success",
      });
      setRows((prev) => prev.filter((row) => !idsToDelete.includes(row.id)));
      setSelectionModel([]);
    } catch (err) {
      console.error("Error deleting CoursePlanningForm entries:", err);
      setSnackbar({
        open: true,
        message: "Error deleting entries. Please try again.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <CcpCpBreadcrumbs pageName="Course Planning Entries" />
      <Card sx={{ width: "100%", mt: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">Course Planning Form Entries</Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={loading || selectionModel.length === 0}
            >
              Delete Selected ({selectionModel.length})
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <AppDataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              autoHeight
              pagination
              pageSize={25}
              pageSizeOptions={[25, 50, 100]}
              checkboxSelection
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(newModel) =>
                setSelectionModel(newModel)
              }
            />
          </Box>
        </CardContent>

        {/* Delete Selected confirmation */}
        <ConfirmDeleteDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
          deleting={deleting}
          title="Delete selected entries?"
          message={`This will permanently delete ${selectionModel.length} entr${
            selectionModel.length === 1 ? "y" : "ies"
          } from the ${COLLECTION_NAME} collection. This action cannot be undone.`}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Card>
    </>
  );
};

export default CoursePlanningFormEntries;
